import type { ToolParameter } from '@/toolSelection/baseTool.store';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { sqDurationStore } from '@/core/core.stores';
import _ from 'lodash';
import type { AnyProperty } from '@/utilities.types';
import type { Capsule } from '@/utilities/datetime.constants';
import {
  BinMode,
  DEFAULT_BIN_WIDTH,
  DEFAULT_NUMBER_OF_BINS,
} from '@/tools/statisticsSummary/statisticsSummary.constants';

export class StatisticsSummaryStore extends BaseToolStore {
  static readonly storeName = 'sqStatisticsSummaryStore';
  type = TREND_TOOLS.STATISTICS_SUMMARY;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    inputSignal: {
      predicate: (parameter: ToolParameter) => (parameter.name ?? '') !== 'condition',
    },
  };

  initialize() {
    this.state = this.immutable({
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      color: '',
      binMode: BinMode.SIZE,
      binWidth: DEFAULT_BIN_WIDTH,
      binWidthIsManual: false,
      numberOfBins: DEFAULT_NUMBER_OF_BINS,
      upperSpecificationLimit: null,
      lowerSpecificationLimit: null,
      target: null,
    });
  }

  get inputSignal() {
    return this.state.get('inputSignal');
  }

  get condition() {
    return this.state.get('condition');
  }

  get window() {
    return this.state.get('window');
  }

  get color(): string {
    return this.state.get('color');
  }

  get binMode(): BinMode {
    return this.state.get('binMode');
  }

  get binWidth(): number {
    return this.state.get('binWidth');
  }

  get numberOfBins(): number {
    return this.state.get('numberOfBins');
  }

  get binWidthIsManual(): boolean {
    return this.state.get('binWidthIsManual');
  }

  get upperSpecificationLimit(): number {
    return this.state.get('upperSpecificationLimit');
  }

  get lowerSpecificationLimit(): number {
    return this.state.get('lowerSpecificationLimit');
  }

  get target(): number {
    return this.state.get('target');
  }

  dehydrate() {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: AnyProperty) {
    this.state.merge(dehydratedState);
  }

  setSampleWindow(payload: { window: Capsule }) {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setColor(payload: { color: string }) {
    this.state.set('color', payload.color);
  }

  setBinMode(payload: { binMode: BinMode }) {
    this.state.set('binMode', payload.binMode);
  }

  setBinWidth(payload: { binWidth: number }) {
    this.state.set('binWidth', payload.binWidth);
  }

  setNumberOfBins(payload: { numberOfBins: number }) {
    this.state.set('numberOfBins', payload.numberOfBins);
  }

  setBinWidthIsManual(payload: { binWidthIsManual: boolean }) {
    this.state.set('binWidthIsManual', payload.binWidthIsManual);
  }

  setUpperSpecificationLimit(payload: { upperSpecificationLimit: number }) {
    this.state.set('upperSpecificationLimit', payload.upperSpecificationLimit);
  }

  setLowerSpecificationLimit(payload: { lowerSpecificationLimit: number }) {
    this.state.set('lowerSpecificationLimit', payload.lowerSpecificationLimit);
  }

  setTarget(payload: { target: number }) {
    this.state.set('target', payload.target);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    SS_SET_COLOR: this.setColor,
    SS_SET_BIN_WIDTH: this.setBinWidth,
    SS_SET_BIN_MODE: this.setBinMode,
    SS_SET_NUMBER_OF_BINS: this.setNumberOfBins,
    SS_SET_SAMPLE_WINDOW: this.setSampleWindow,
    SS_SET_UPPER_SPECIFICATION_LIMIT: this.setUpperSpecificationLimit,
    SS_SET_LOWER_SPECIFICATION_LIMIT: this.setLowerSpecificationLimit,
    SS_SET_TARGET: this.setTarget,
    SS_SET_BIN_WIDTH_IS_MANUAL: this.setBinWidthIsManual,
  };
}
